import { useApiError } from "@raiden/library/components/ReactHookForm/ApiError";
import {
  HOLDERS_STATES_VALUE_DISABLED,
  HOLDERS_STATES_VALUE_ENABLED,
  HOLDERS_TYPES_VALUE_CHILD,
} from "@raiden/library/constants/holders";
import { initString } from "@raiden/library/helpers/form/initString";
import { usePreferredYear } from "@raiden/library/hooks/usePreferredYear";
import useRequest from "@raiden/library/hooks/useRequest";
import generateApiUri from "@raiden/library/libraries/utils/generateApiUri";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useIntl } from "react-intl";

export const WIZARD_HOLDERS_FORM_ID = "wizard-holders-form";

/**
 * @typedef {object} WizardHolderFormData
 * @property {"update" | "delete"} _change
 * @property {number} holder_id
 * @property {import("@raiden/library/types/Customer").CustomerGender | ""} gender
 * @property {string} first_name
 * @property {string} last_name
 * @property {string} birthdate
 * @property {import("@raiden/library/types/Holder").HolderDiet | ""} diet
 * @property {string} comment
 * @property {{year_id: number, school_id: string, level: string}} schooling
 */

/**
 * @param {object} params
 * @param {import("@raiden/library/types/Holder").Holder} params.holder
 * @param {import("@raiden/library/types/Configuration").ConfigurationCurrentYear} params.preferredYear
 *
 * @returns {WizardHolderFormData}
 */
export function getWizardHolderFormData({ holder, preferredYear }) {
  const schooling = holder.schoolings?.find(
    (schooling) => schooling.year_id === preferredYear?.id,
  );
  return {
    _change:
      holder.state === HOLDERS_STATES_VALUE_DISABLED ? "delete" : "update",
    holder_id: holder.id,
    gender: holder.gender,
    first_name: holder.first_name,
    last_name: holder.last_name,
    birthdate: holder.birthdate,
    diet: holder.diet,
    comment: initString(holder.comment),
    schooling: {
      year_id: preferredYear?.id,
      school_id: schooling?.school_id ? String(schooling.school_id) : "",
      level: schooling?.level ?? "",
    },
  };
}

/**
 * @typedef {object} WizardHoldersFormValues
 * @property {WizardHolderFormData[]} data
 */

/**
 * @param {object} params
 * @param {import("@raiden/library/types/Holder").Holder[]} params.holders
 * @param {import("@raiden/library/types/Configuration").ConfigurationCurrentYear | null} params.preferredYear
 *
 * @returns {WizardHoldersFormValues}
 */
function getDefaultValues({ holders, preferredYear }) {
  return {
    data: preferredYear
      ? holders.map((holder) =>
          getWizardHolderFormData({ holder, preferredYear }),
        )
      : [],
  };
}

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/Holder").Holder[]} holders
 * @property {() => void} onSuccess
 * @property {(params: {
 * isLoading: boolean,
 * form: import("react-hook-form").UseFormReturn<any>,
 * fields: any[],
 * append: import("react-hook-form").UseFieldArrayAppend<WizardHoldersFormValues, "data">,
 * }) => import("react").ReactElement} render
 *
 * @param {Props} props
 */
function Handler({ holders, onSuccess, render }) {
  const intl = useIntl();

  const preferredYear = usePreferredYear();

  const form = useForm({
    defaultValues: getDefaultValues({ holders, preferredYear }),
  });
  const { control, handleSubmit } = form;

  const { fields, append } = useFieldArray({
    control,
    name: "data",
  });

  const { request, isLoading, toastSuccess } = useRequest();

  const { resolveApiError, resetApiErrors } = useApiError();

  /**
   * @param {WizardHoldersFormValues} values
   */
  function onSubmit(values) {
    request(
      generateApiUri({
        id: "@holders.batch",
      }),
      {
        method: "POST",
        body: {
          data: values.data.map((holderValues) => {
            // disable holder
            if (holderValues._change === "delete") {
              return {
                id: holderValues.holder_id,
                state: HOLDERS_STATES_VALUE_DISABLED,
              };
            }

            // update holder
            const holder = holders.find(
              (holder) => holder.id === holderValues.holder_id,
            );
            return {
              ...holderValues,
              _change: undefined,
              id: holderValues.holder_id,
              state: HOLDERS_STATES_VALUE_ENABLED,
              schooling:
                holder?.type === HOLDERS_TYPES_VALUE_CHILD
                  ? holderValues.schooling
                  : undefined,
            };
          }),
        },
      },
    )
      .then((response) => {
        resetApiErrors();
        toastSuccess({
          title: intl.formatMessage({
            defaultMessage: "Les modifications ont bien été prises en compte.",
          }),
        });
        onSuccess();
      })
      .catch((response) => {
        resolveApiError({ response, form });
      });
  }

  return (
    <FormProvider {...form}>
      <form id={WIZARD_HOLDERS_FORM_ID} onSubmit={handleSubmit(onSubmit)}>
        {render({
          isLoading: isLoading,
          form,
          fields,
          append,
        })}
      </form>
    </FormProvider>
  );
}

export default Handler;
